import React, { useEffect, useState } from 'react';
import { Modal } from '../../Common';
import { Checkbox } from '../../Common/Checkbox';
import { useHttpGetUsers, useOrganizationId, useHttpTransferOwnership } from '../../../hooks';
import { Button, IMenuItem, SearchableDropdown } from '../../Common';
import { useTranslation } from '../../../hooks/useTranslation';
import { useBeforeUnloadListener } from '../../../hooks/windowEvents/useBeforeUnloadListener';
import TransferOwnershipLoader from '../../../views/Settings/TransferOwnershipLoader';
import { RoleTypeEnum } from '../../../commonTypes/user.types';

const TransferOwnershipModal = ({ isVisible, closeModal }: { isVisible: boolean; closeModal: () => void }) => {
    const organizationId = useOrganizationId();
    const { t } = useTranslation();

    const { data: members } = useHttpGetUsers(organizationId);
    const { exec: transferOwnership } = useHttpTransferOwnership(organizationId);

    const [isChecked, setIsChecked] = useState(false);
    const [isLoaderDisplayed, setLoaderDisplayed] = useState(false);
    const [menuItems, setMenuItems] = useState<IMenuItem[]>([]);
    const [selectedMember, setSelectedMember] = useState<IMenuItem | undefined>({ label: '', value: '' });

    useBeforeUnloadListener(true);

    useEffect(() => {
        const filteredMembers = members
            .filter(member => member.role.name !== RoleTypeEnum.OWNER)
            .map(member => ({
                label: `${member.givenName} ${member.familyName}`,
                value: member.userSub,
            }));

        setMenuItems(filteredMembers);
    }, [members]);

    const handleDropdownChange = (item: IMenuItem) => {
        setSelectedMember(item);
    };

    const handleSubmit = () => {
        if (selectedMember) {
            transferOwnership(String(selectedMember.value));
            setLoaderDisplayed(true);
        }
    };

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsChecked(e.target.checked);
    };

    if (isLoaderDisplayed) {
        return <TransferOwnershipLoader />;
    }

    return (
        <Modal isVisible={isVisible} closeModal={closeModal}>
            <div className="transfer-ownership-modal">
                <div className="transfer-ownership-modal__title-container">
                    <h3 className="transfer-ownership-modal__title">{t('settings.transferOwnershipTitle')}</h3>
                </div>
                <div className="transfer-ownership-modal__content">
                    <p className="paragraph-4">
                        {t('settings.transferOwnershipModalMessage1')}
                        <b>{t('settings.transferOwnershipModalMessage2')}</b>
                    </p>

                    <p className="paragraph-4">{t('settings.transferOwnershipModalMessage3')}</p>

                    <p className="paragraph-4">{t('settings.transferOwnershipModalMessage4')}</p>

                    <Checkbox
                        id="confirm-understanding"
                        label={t('settings.understand')}
                        checked={isChecked}
                        handleChange={handleCheckboxChange}
                        className="checkbox-n transfer-ownership-modal__checkbox"
                    />

                    <div className="transfer-ownership-modal__dropdown">
                        <SearchableDropdown
                            label={t('settings.selectNewOwner')}
                            menuItems={menuItems}
                            selected={selectedMember}
                            onChange={handleDropdownChange}
                            placeholder={t('common.search')}
                            disabled={!isChecked}
                        />
                    </div>
                </div>

                <div className="change-branch__buttons">
                    <Button variant="text" onClick={closeModal} text={t('common.cancel')} color="blue" />
                    <Button variant="danger" onClick={handleSubmit} text={t('settings.transferButton')} disabled={!isChecked || !selectedMember} />
                </div>
            </div>
        </Modal>
    );
};

export default TransferOwnershipModal;
